import { Stack } from '@mantine/core';
import { GetTuyaDeviceResponse } from 'api/actions/get-tuya-device/get-tuya-device-response';
import SensorInfo from 'components/SensorInfo';
import SensorStatistics from 'components/SensorStatistics';
import { useMemo, useState } from 'react';
import { createLineChartOptions, lineChartGradientBackground } from 'pages/sensors/chartUtils';
import { calculateTemperatureAverage, filterMeasurementsForRange } from 'pages/tuya-device/tuyaThermostatUtils';

type ThermostatDetails = NonNullable<GetTuyaDeviceResponse['details']>;
type RangeName = keyof ThermostatDetails['ranges'];

const RANGE_TITLES: Record<RangeName, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  lastWeek: 'Last week',
};

/** Formats one API-provided chart range in the selected browser locale. */
function formatRange(range: { start: string; end: string }, locale: string, timezone: string, daily: boolean) {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: 'numeric',
    month: 'short',
    ...(daily ? { year: 'numeric' } : {}),
  });

  if (daily) {
    return formatter.format(new Date(range.start));
  }

  return `${formatter.format(new Date(range.start))} – ${formatter.format(new Date(range.end))}`;
}

/** Displays the read-only temperature summary and raw measurement chart for a Tuya thermostat. */
export default function TuyaThermostatDetail({
  device,
  lang,
  _t,
}: {
  device: GetTuyaDeviceResponse;
  lang: string;
  _t: (msgid: string) => string;
}) {
  const details = device.details as ThermostatDetails;
  const [selectedTitle, setSelectedTitle] = useState(RANGE_TITLES.today);

  const views = useMemo(
    () =>
      (Object.keys(RANGE_TITLES) as RangeName[]).map((rangeName) => {
        const range = details.ranges[rangeName];
        const isDaily = rangeName === 'today' || rangeName === 'yesterday';
        const shortFormatter = new Intl.DateTimeFormat(lang, {
          timeZone: details.timezone,
          ...(isDaily ? {} : { weekday: 'short', day: 'numeric' }),
          hour: '2-digit',
          minute: '2-digit',
        });
        const longFormatter = new Intl.DateTimeFormat(lang, {
          timeZone: details.timezone,
          dateStyle: 'medium',
          timeStyle: 'short',
        });

        return {
          title: RANGE_TITLES[rangeName],
          range: formatRange(range, lang, details.timezone, isDaily),
          data: filterMeasurementsForRange(details.measurements, range).map(({ measuredAt, value }) => ({
            value,
            unit: details.unit,
            labelShort: shortFormatter.format(new Date(measuredAt)),
            labelLong: longFormatter.format(new Date(measuredAt)),
          })),
        };
      }),
    [details, lang]
  );

  const selectedView = views.find(({ title }) => title === selectedTitle) || views[0];
  const rawAverage = calculateTemperatureAverage(selectedView.data);
  const average = rawAverage === null ? '—' : rawAverage.toFixed(2);
  const dense = selectedView.data.length > 48;
  const current = details.currentTemperature;
  const lastUpdate = current
    ? new Intl.DateTimeFormat(lang, {
        timeZone: details.timezone,
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(current.measuredAt))
    : _t('No data available');
  const sensor = {
    id: device.id,
    name: device.label || _t('Thermostat'),
    mainBox: {
      icon: '/img/sensor-icons/temperature.svg',
      value: current?.value ?? null,
      unit: details.unit,
      headline: _t('Current temperature'),
      subHeadline: current ? `${_t('Last update')}: ${lastUpdate}` : lastUpdate,
    },
    chart: {
      graphType: 'line' as const,
      title: _t('Average temperature'),
      views,
    },
  };
  const chartData = selectedView.data.length
    ? {
        labels: selectedView.data.map(({ labelShort }) => labelShort),
        datasets: [
          {
            barPercentage: 0.6,
            label: _t('Temperature'),
            data: selectedView.data.map(({ value }) => value),
            backgroundColor: 'rgba(85, 31, 255, 0.4)',
            hoverBackgroundColor: '#1F212A',
            borderRadius: 5,
            borderSkipped: false,
            type: 'line' as const,
            borderColor: '#551FFF',
            borderWidth: 4,
            tension: 0.1,
            pointBackgroundColor: '#FFFFFF',
            pointBorderColor: '#000000',
            pointBorderWidth: dense ? 0 : 3,
            pointRadius: dense ? 0 : 4,
            pointHoverRadius: 6,
          },
        ],
      }
    : null;

  return (
    <Stack gap={16}>
      <SensorInfo sensor={sensor} lang={lang} />
      <SensorStatistics
        calculateAverageValue={average}
        calculateAggregateValue={average}
        sensor={sensor}
        getRangeForSelectedTitle={(title) => views.find((view) => view.title === title)?.range}
        selectedTitle={selectedTitle}
        setSelectedTitle={(title) => setSelectedTitle(title || RANGE_TITLES.today)}
        titles={views.map(({ title }) => title)}
        chartData={chartData}
        chartOptions={createLineChartOptions(sensor, selectedTitle)}
        backgroundBar={lineChartGradientBackground}
        _t={_t}
      />
    </Stack>
  );
}
